import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { ReactElement, useCallback, useMemo } from "react";

import { Button } from "./catalyst/Button";
import { Sidebar } from "./catalyst/Sidebar";
import { SidebarBody } from "./catalyst/SidebarBody";
import { SidebarHeader } from "./catalyst/SidebarHeader";
import { SidebarLabel } from "./catalyst/SidebarLabel";
import {
  getPersistentBooleanAtom,
  SidebarBehavior,
  SidebarBehaviorContext,
  useSidebarBehavior,
} from "./collapsible-sidebar-behavior";

export const CollapsibleSidebar = ({
  children,
  defaultCollapsed = false,
  header,
  icon,
  storageKey = "app.sidebar.collapsed",
  title = "App",
}: {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  header?: React.ReactNode; // lets you fully control the header UI
  icon?: React.ReactNode;
  storageKey?: string;
  title?: string;
}): ReactElement => {
  const $collapsed = useMemo(
    () => getPersistentBooleanAtom(storageKey, defaultCollapsed),
    [storageKey, defaultCollapsed],
  );
  const persistedCollapsed = useStore($collapsed);
  const collapsed = persistedCollapsed;

  const toggleCollapsed = useCallback(() => {
    $collapsed.set(!persistedCollapsed);
  }, [$collapsed, persistedCollapsed]);

  const showLabels = !collapsed;

  const labelClassName = useMemo(
    () =>
      clsx(
        "transition-all duration-200",
        showLabels ? "w-auto opacity-100" : "w-0 overflow-hidden opacity-0",
      ),
    [showLabels],
  );

  const sidebarWidthClassName = collapsed ? "w-16" : "w-56";

  const itemTitle = useCallback(
    (label: string) => (collapsed ? label : undefined),
    [collapsed],
  );

  const value: SidebarBehavior = useMemo(
    () => ({
      collapsed,
      itemTitle,
      labelClassName,
      showLabels,
      sidebarWidthClassName,
      toggleCollapsed,
    }),
    [
      collapsed,
      showLabels,
      labelClassName,
      sidebarWidthClassName,
      itemTitle,
      toggleCollapsed,
    ],
  );

  return (
    <SidebarBehaviorContext.Provider value={value}>
      <Sidebar
        className={clsx(
          "transition-[width] duration-200 ease-in-out",
          sidebarWidthClassName,
        )}
      >
        {/* Default header (you can override via `header`) */}
        {header ?? <DefaultSidebarHeader icon={icon} title={title} />}
        <SidebarBody>{children}</SidebarBody>
      </Sidebar>
    </SidebarBehaviorContext.Provider>
  );
};

const DefaultSidebarHeader = ({
  icon,
  title,
}: {
  icon?: React.ReactNode;
  title: string;
}): ReactElement => {
  const { collapsed, labelClassName, toggleCollapsed } = useSidebarBehavior();

  return (
    <SidebarHeader
      className={clsx(
        "flex !flex-row items-center gap-2 !border-b-0",
        collapsed && "justify-center",
      )}
    >
      {/* Logo and title on the left; when collapsed, hidden */}
      <div
        className={clsx(
          "flex min-w-0 flex-1 items-center gap-2 transition-all duration-200",
          collapsed && "w-0 overflow-hidden opacity-0",
        )}
      >
        {icon ? (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            {icon}
          </div>
        ) : null}
        <div
          className={clsx(
            "min-w-0 transition-all duration-200",
            labelClassName,
          )}
        >
          <SidebarLabel className="truncate text-sm font-normal text-zinc-950 dark:text-white">
            {title}
          </SidebarLabel>
        </div>
      </div>

      {/* Chevron on the right: collapse when expanded, expand when collapsed */}
      <Button
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="shrink-0"
        onClick={toggleCollapsed}
        plain
      >
        {collapsed ? (
          <ChevronRightIcon className="size-5" />
        ) : (
          <ChevronLeftIcon className="size-5" />
        )}
      </Button>
    </SidebarHeader>
  );
};
